import React, { useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip';
import { Box } from "@atlaskit/primitives/compiled";

import { AddCommentPromptButton } from '@confluence/comments-common/entry-points/AddCommentPromptButton.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { UNIVERSAL_OPEN_COMMENT_EDITOR } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

const i18n = defineMessages({
	addCommentTooltip: {
		id: 'object-comments.footer.add-button-tooltip',
		defaultMessage: 'Add comment',
		description:
			'Tooltip message for adding a comment, used in conjunction with shortcut visualizer',
	},
});

export const ObjectAddCommentPrompt = ({
	onClick,
	autofocusRef,
}: {
	onClick: () => void;
	autofocusRef: { current: boolean | null };
}) => {
	const { formatMessage } = useIntl();

	const addCommentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_OPEN_COMMENT_EDITOR,
	});

	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			(buttonRef.current as HTMLElement)?.focus({
				preventScroll: true,
			});
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	return (
		<Box>
			<Tooltip
				content={
					expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true)
						? formatMessage(i18n.addCommentTooltip)
						: undefined
				}
				shortcut={addCommentShortcutStrings}
				position="top"
			>
				<AddCommentPromptButton
					onClick={onClick}
					autofocusRef={autofocusRef}
					testId="add-comment-button"
				/>
			</Tooltip>
		</Box>
	);
};
